/**
 * Shared formatting + status helpers for every condition-monitoring screen
 * (motor, energy, and future ones). Centralised so a numeric-safety or
 * live-threshold change is made in exactly one place — no per-screen copies.
 */

/** How long after the last update a source is still considered "live". */
export const LIVE_THRESHOLD_MS = 10_000;

export interface FormatMetricOptions {
  /** Decimal places (default 1). */
  decimals?: number;
  /** Unit suffix, e.g. "Nm" — rendered as " Nm" (default none). */
  unit?: string;
  /** Rendered when the value is missing / not numeric (default "—"). */
  fallback?: string;
}

/**
 * Format a metric value for display. Tolerant of the messy inputs seen across
 * edge servers: `undefined`, numeric strings ("12.5"), or genuinely
 * non-numeric values all resolve safely instead of throwing (this is the fix
 * for the `s.torqueRms.toFixed is not a function` class of crashes).
 */
export function formatMetricValue(
  value: unknown,
  opts: FormatMetricOptions = {},
): string {
  const { decimals = 1, unit, fallback = '—' } = opts;
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(n)) return fallback;
  const body = n.toFixed(decimals);
  return unit ? `${body} ${unit}` : body;
}

/** True when `lastUpdate` (ms epoch) is within the live threshold of now. */
export function isLiveWithin(
  lastUpdate: number,
  thresholdMs: number = LIVE_THRESHOLD_MS,
  now: number = Date.now(),
): boolean {
  return now - lastUpdate < thresholdMs;
}
